import useTranslation from '@/utils/hooks/translation/useTranslation';
//hooks
import useAcl from '@/utils/hooks/user/useAcl';

//lib
import { aclFeatures } from '@/lib/acl/aclFeatures';

//components
import Icon from '@/components/base/Icon';
import Iconosquare from '@/components/icons/Iconosquare';
import { ZapierIcon } from '@/components/icons/Integrations/Zapier';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';

type OverviewProps = { onClick: React.Dispatch<React.SetStateAction<'zapier' | 'api_key'>> };

export function Overview({ onClick }: OverviewProps) {
    const { t } = useTranslation('integrations');
    const { hasAcl } = useAcl();

    const hasZapierAccess = hasAcl(aclFeatures.api.zapier.read);
    const hasPublicApiAccess = hasAcl(aclFeatures.api.base.read);

    return (
        <div
            className="flex w-full flex-col space-y-8 @container"
            data-testid="integrations-overview">
            <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
                {hasZapierAccess ? (
                    <span className="@container">
                        <button
                            disabled={!hasZapierAccess}
                            onClick={() => onClick('zapier')}
                            className="flex w-full flex-col items-center gap-x-7 gap-y-3.5 rounded-xl border border-icoGray-300 bg-icoGray-100 p-1 focus:ring-0 focus:ring-offset-0 @xs:flex-row dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack"
                            data-testid="zapier-button">
                            <span className="flex h-36 min-w-[144px] items-center justify-center rounded-lg bg-icoGray-300 dark:bg-icoDarkMode-beton">
                                <ZapierIcon width={87} />
                            </span>
                            <span className="flex flex-col items-center space-y-2.5 text-xs @xs:items-start @xs:pr-6">
                                <span className="flex flex-col items-center space-y-0.5 @xs:items-start">
                                    <span className="font-GBold text-xl">Zapier</span>
                                    <span className="line-clamp-5 text-center font-GMedium text-icoGray-500 @xs:text-left dark:text-icoDarkMode-wolf">
                                        {t('overview.zapier_description')}
                                    </span>
                                </span>
                                <span className="flex items-center gap-2 text-left text-icoBlue">
                                    <span>{t('overview.new_integration')}</span>
                                    <Icon icon="arrow-right" />
                                </span>
                            </span>
                        </button>
                    </span>
                ) : null}
                <TooltipPlanRestriction disabled={hasPublicApiAccess} position="top">
                    <span className="@container/item">
                        <button
                            disabled={!hasPublicApiAccess}
                            onClick={() => onClick('api_key')}
                            className="flex w-full flex-col items-center gap-x-7 gap-y-3.5 rounded-xl border border-icoGray-300 bg-icoGray-100 p-1 focus:ring-0 focus:ring-offset-0 @xs:flex-row dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack"
                            data-testid="api-access-button">
                            <span className="flex h-36 min-w-[144px] items-center justify-center rounded-lg bg-icoGray-300 dark:bg-icoDarkMode-beton">
                                <span className="flex items-center gap-2">
                                    <Iconosquare className="w-4 text-icoBlue" />
                                    <span className="text-xs dark:text-white">iconosquare</span>
                                </span>
                            </span>
                            <span className="flex flex-col items-center space-y-2.5 text-xs @xs:items-start @xs:pr-6">
                                <span className="flex flex-col items-center space-y-0.5 @xs:items-start">
                                    <span className="flex items-center gap-2 font-GBold text-xl">
                                        {t('api_access.title')}
                                        {!hasPublicApiAccess ? (
                                            <Icon icon="crown" className="text-icoBlue" />
                                        ) : null}
                                    </span>
                                    <span className="line-clamp-5 text-center font-GMedium text-icoGray-500 @xs:text-left dark:text-icoDarkMode-wolf">
                                        {t('api_access.description')}
                                    </span>
                                </span>
                                <span className="flex items-center gap-2 text-left text-icoBlue">
                                    <span>{t('overview.generate_key')}</span>
                                    <Icon icon="arrow-right" />
                                </span>
                            </span>
                        </button>
                    </span>
                </TooltipPlanRestriction>
            </div>
        </div>
    );
}
